/**
 * Bulk Upload — Orchestration Hook
 *
 * Manages the entire bulk upload wizard state machine:
 * 1. Select Template → 2. Upload File → 3. Map Columns →
 * 4. Resolve Relations → 5. Validate & Preview → 6. Submit
 *
 * Follows the project's hook patterns (useFetch), uses native fetch for mutations,
 * and stores auth tokens from sessionStorage.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { CustomFieldDefinition } from '../../../@types';
import {
	BulkUploadStep,
	BULK_UPLOAD_STEPS,
	ColumnMapping,
	LookupData,
	ResolutionGroup,
	UploadProgress,
	UploadSchema,
	UploadTemplate,
	ValidationResult,
	BulkRowResult,
	TemplateMappingEntry,
} from './utils/types';
import { autoMatchColumns, applyTemplate } from './utils/autoMatch';
import { buildResolutionGroups, applyResolutions } from './utils/resolve';
import {
	validateRows,
	getValidationStats,
	getSubmittableRows,
	revalidateRow,
} from './utils/validate';
import { parseWorksheet, ParseResult } from './utils/parseWorksheet';
import { downloadTemplate } from './utils/templateGenerator';
import { schemaWithCustomFields, extractCustomData } from './utils/customFields';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseBulkUploadParams {
	/** The schema for the entity being uploaded */
	schema: UploadSchema;
	/** Project ID (injected from ContextProject) */
	projectId: number;
	/** Lookup data for relation resolution */
	lookupData: LookupData;
	/** Project custom field definitions (loaded from project.customFieldDefinitions) */
	customFieldDefinitions?: CustomFieldDefinition[];
	/** Callback when upload completes successfully */
	onSuccess?: () => void;
	/** Optional callback after an inline related record is created (e.g., refresh activities list). */
	onLookupDataRefresh?: (contextKey: string) => Promise<void> | void;
	/** Callback on close/cancel */
	onClose?: () => void;
	/** Batch size for submission (default 25) */
	batchSize?: number;
}

export interface UseBulkUploadReturn {
	// Step navigation
	currentStep: BulkUploadStep;
	currentStepIndex: number;
	canGoNext: boolean;
	canGoBack: boolean;
	goNext: () => void;
	goBack: () => void;
	goToStep: (step: BulkUploadStep) => void;
	reset: () => void;

	// Step 1: Template
	templates: UploadTemplate[];
	selectedTemplate: UploadTemplate | null;
	isLoadingTemplates: boolean;
	selectTemplate: (template: UploadTemplate | null) => void;
	loadTemplates: () => Promise<void>;
	handleDownloadTemplate: () => Promise<void>;

	// Step 2: Upload
	file: File | null;
	parseResult: ParseResult | null;
	isParsingFile: boolean;
	parseError: string | null;
	handleFileSelect: (file: File) => Promise<void>;
	handleSheetChange: (sheetName: string) => Promise<void>;
	handleHeaderRowChange: (row: number) => void;

	// Step 3: Map columns
	mappings: ColumnMapping[];
	updateMapping: (targetField: string, sourceColumn: string | null) => void;

	// Step 4: Resolve relations
	resolutionGroups: ResolutionGroup[];
	resolveGroup: (key: string, selectedId: number | null, selectedLabel: string | null) => void;
	skipGroup: (key: string) => void;
	autoResolveHighConfidence: () => void;
	unresolvedCount: number;
	createRelatedRecord: (
		groupKey: string,
		contextKey: string,
		apiEndpoint: string,
		payload: Record<string, unknown>,
		payloadTransform?: (
			formValues: Record<string, unknown>,
			projectId: number
		) => Record<string, unknown>
	) => Promise<{ id: number; name: string }>;
	mergedLookupData: LookupData;

	// Step 5: Validate & Preview
	validationResults: ValidationResult[];
	validationStats: ReturnType<typeof getValidationStats> | null;
	toggleRowSkip: (rowIndex: number) => void;
	editCell: (rowIndex: number, fieldName: string, newValue: unknown) => void;

	// Step 6: Submit
	progress: UploadProgress;
	submit: () => Promise<void>;
	saveTemplate: (name: string, description?: string) => Promise<void>;
	retryFailed: () => void;
	exportFailedRows: () => void;

	// Global
	error: string | null;
	clearError: () => void;

	// Effective schema (base + custom fields)
	effectiveSchema: UploadSchema;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const BATCH_SIZE_DEFAULT = 25;

export function useBulkUpload({
	schema,
	projectId,
	lookupData,
	customFieldDefinitions,
	onSuccess,
	onLookupDataRefresh,
	onClose,
	batchSize = BATCH_SIZE_DEFAULT,
}: UseBulkUploadParams): UseBulkUploadReturn {
	// ---- Effective schema (base + custom fields) ----
	const effectiveSchema = useMemo(
		() => schemaWithCustomFields(schema, customFieldDefinitions ?? []),
		[schema, customFieldDefinitions]
	);

	// ---- Step state ----
	const [currentStepIndex, setCurrentStepIndex] = useState(0);
	const currentStep = BULK_UPLOAD_STEPS[currentStepIndex];

	// ---- Step 1: Template ----
	const [templates, setTemplates] = useState<UploadTemplate[]>([]);
	const [selectedTemplate, setSelectedTemplate] = useState<UploadTemplate | null>(null);
	const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

	// ---- Step 2: Upload ----
	const [file, setFile] = useState<File | null>(null);
	const [parseResult, setParseResult] = useState<ParseResult | null>(null);
	const [isParsingFile, setIsParsingFile] = useState(false);
	const [parseError, setParseError] = useState<string | null>(null);

	// ---- Step 3: Mappings ----
	const [mappings, setMappings] = useState<ColumnMapping[]>([]);

	// ---- Step 4: Resolution ----
	const [resolutionGroups, setResolutionGroups] = useState<ResolutionGroup[]>([]);
	const [createdLookups, setCreatedLookups] = useState<LookupData>({});

	// ---- Step 5: Validation ----
	const [validationResults, setValidationResults] = useState<ValidationResult[]>([]);

	// ---- Step 6: Submit ----
	const [progress, setProgress] = useState<UploadProgress>({
		status: 'idle',
		totalRows: 0,
		processedRows: 0,
		successfulRows: 0,
		failedRows: 0,
		skippedRows: 0,
		currentBatch: 0,
		totalBatches: 0,
		results: [],
	});

	// ---- Global ----
	const [error, setError] = useState<string | null>(null);
	const abortRef = useRef<AbortController | null>(null);

	// ---- Auth helpers ----
	const getHeaders = useCallback(() => {
		const accessToken = sessionStorage.getItem('accessToken');
		const idToken = sessionStorage.getItem('idToken');
		return {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${accessToken}`,
			User: String(idToken),
		};
	}, []);

	const apiUrl = process.env.REACT_APP_API_URL;
	// endpoints live under /api/v2, not /api/v1
	const apiUrlV2 = apiUrl?.replace(/\/v1\/?$/, '') || apiUrl;

	// Merge external lookupData with records created inline during resolution
	const mergedLookupData: LookupData = useMemo(() => {
		const merged: LookupData = { ...lookupData };
		for (const [key, items] of Object.entries(createdLookups)) {
			merged[key] = [...(merged[key] ?? []), ...items];
		}
		return merged;
	}, [lookupData, createdLookups]);

	// ====================================================================
	// Step 1: Template Management
	// ====================================================================

	const loadTemplates = useCallback(async () => {
		setIsLoadingTemplates(true);
		try {
			const res = await fetch(
				`${apiUrlV2}/v2/upload-templates?project=${projectId}&entityType=${schema.entityType}`,
				{ headers: getHeaders() }
			);
			const data = await res.json();
			if (data.error) throw new Error(data.error.message);
			setTemplates(data.data ?? []);
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to load templates');
		} finally {
			setIsLoadingTemplates(false);
		}
	}, [apiUrlV2, getHeaders, projectId, schema.entityType]);

	const selectTemplate = useCallback((template: UploadTemplate | null) => {
		setSelectedTemplate(template);
	}, []);

	const handleDownloadTemplate = useCallback(async () => {
		await downloadTemplate({
			schema: effectiveSchema,
			lookupData,
		});
	}, [effectiveSchema, lookupData]);

	// ====================================================================
	// Step 2: File Upload & Parse
	// ====================================================================

	const handleFileSelect = useCallback(
		async (selectedFile: File) => {
			setFile(selectedFile);
			setIsParsingFile(true);
			setParseError(null);
			try {
				const result = await parseWorksheet(selectedFile, {
					fields: effectiveSchema.fields,
					lookupData,
				});
				setParseResult(result);

				// Auto-match columns
				let autoMappings: ColumnMapping[];
				if (selectedTemplate) {
					autoMappings = applyTemplate(
						selectedTemplate.columnMappings,
						result.headers,
						effectiveSchema.fields
					);
				} else {
					autoMappings = autoMatchColumns(result.headers, effectiveSchema.fields);
				}
				setMappings(autoMappings);
			} catch (err) {
				setParseError(err instanceof Error ? err.message : 'Failed to parse file');
			} finally {
				setIsParsingFile(false);
			}
		},
		[effectiveSchema, lookupData, selectedTemplate]
	);

	const handleSheetChange = useCallback(
		async (sheetName: string) => {
			if (!file) return;
			setIsParsingFile(true);
			try {
				const result = await parseWorksheet(file, {
					sheetName,
					fields: effectiveSchema.fields,
					lookupData,
				});
				setParseResult(result);
				const autoMappings = autoMatchColumns(result.headers, effectiveSchema.fields);
				setMappings(autoMappings);
			} catch (err) {
				setParseError(err instanceof Error ? err.message : 'Failed to parse sheet');
			} finally {
				setIsParsingFile(false);
			}
		},
		[file, effectiveSchema, lookupData]
	);

	const handleHeaderRowChange = useCallback(
		(row: number) => {
			if (!file) return;
			setIsParsingFile(true);
			parseWorksheet(file, {
				headerRow: row,
				sheetName: parseResult?.selectedSheet,
				fields: effectiveSchema.fields,
				lookupData,
			})
				.then((result) => {
					setParseResult(result);
					const autoMappings = autoMatchColumns(result.headers, effectiveSchema.fields);
					setMappings(autoMappings);
				})
				.catch((err) => setParseError(err instanceof Error ? err.message : 'Failed to parse'))
				.finally(() => setIsParsingFile(false));
		},
		[file, parseResult?.selectedSheet, effectiveSchema, lookupData]
	);

	// ====================================================================
	// Step 3: Column Mapping
	// ====================================================================

	const updateMapping = useCallback((targetField: string, sourceColumn: string | null) => {
		setMappings((prev) =>
			prev.map((m) =>
				m.targetField === targetField ? { ...m, sourceColumn, autoMatched: false } : m
			)
		);
	}, []);

	// ====================================================================
	// Step 4: Resolve Relations
	// ====================================================================

	const runRelationResolution = useCallback(() => {
		if (!parseResult) return;

		// First validate to get resolvedRows with __unresolved: placeholders
		const results = validateRows(effectiveSchema, parseResult.rows, {
			mappings,
			contextDefaults: parseResult.contextDefaults,
		});
		setValidationResults(results);

		// Build resolution groups from raw spreadsheet rows
		const groups = buildResolutionGroups(
			parseResult.rows,
			effectiveSchema.fields,
			mappings,
			mergedLookupData
		);
		setResolutionGroups(groups);
	}, [parseResult, effectiveSchema, mappings, mergedLookupData]);

	const resolveGroup = useCallback(
		(key: string, selectedId: number | null, selectedLabel: string | null) => {
			setResolutionGroups((prev) =>
				prev.map((g) =>
					g.key === key
						? {
								...g,
								resolution: {
									status: selectedId !== null ? 'accepted' : 'manual',
									selectedId,
									selectedLabel,
								},
							}
						: g
				)
			);
		},
		[]
	);

	const skipGroup = useCallback((key: string) => {
		setResolutionGroups((prev) =>
			prev.map((g) =>
				g.key === key
					? { ...g, resolution: { status: 'skipped', selectedId: null, selectedLabel: null } }
					: g
			)
		);
	}, []);

	const autoResolveHighConfidence = useCallback(() => {
		setResolutionGroups((prev) =>
			prev.map((g) => {
				if (
					g.resolution.status === 'pending' &&
					(g.scoreResult.confidence === 'exact' || g.scoreResult.confidence === 'high') &&
					g.scoreResult.resolvedId !== null
				) {
					return {
						...g,
						resolution: {
							status: 'accepted',
							selectedId: g.scoreResult.resolvedId,
							selectedLabel: g.scoreResult.candidates[0]?.label ?? null,
						},
					};
				}
				return g;
			})
		);
	}, []);

	/**
	 * Create a new record for an unresolved relation inline.
	 * POSTs to the target entity's API endpoint, injects the new record into
	 * mergedLookupData, and auto-resolves the group.
	 */
	const createRelatedRecord = useCallback(
		async (
			groupKey: string,
			contextKey: string,
			apiEndpoint: string,
			payload: Record<string, unknown>,
			payloadTransform?: (
				formValues: Record<string, unknown>,
				projectId: number
			) => Record<string, unknown>
		): Promise<{ id: number; name: string }> => {
			const body = payloadTransform
				? payloadTransform(payload, projectId)
				: { ...payload, projectId };
			const res = await fetch(`${apiUrlV2}${apiEndpoint}`, {
				method: 'POST',
				headers: getHeaders(),
				body: JSON.stringify(body),
			});
			const data = await res.json();

			if (data.error) {
				throw new Error(data.error.message || 'Failed to create record');
			}

			const created = data.data ?? data;
			const newId: number = created.id;
			const newName: string =
				created.name ??
				created.num ??
				created.description ??
				payload.name ??
				payload.num ??
				String(newId);

			// Inject into local lookup overlay
			setCreatedLookups((prev) => ({
				...prev,
				[contextKey]: [...(prev[contextKey] ?? []), { id: newId, name: newName }],
			}));

			// Refresh upstream lookup sources when requested (e.g., ContextSpendPlan activities).
			if (onLookupDataRefresh) {
				await onLookupDataRefresh(contextKey);
			}

			// Auto-resolve the group
			setResolutionGroups((prev) =>
				prev.map((g) =>
					g.key === groupKey
						? {
								...g,
								resolution: {
									status: 'created' as const,
									selectedId: newId,
									selectedLabel: newName,
								},
							}
						: g
				)
			);

			return { id: newId, name: newName };
		},
		[apiUrlV2, getHeaders, onLookupDataRefresh, projectId]
	);

	// ====================================================================
	// Step 5: Validate & Preview
	// ====================================================================

	const runFinalValidation = useCallback(() => {
		if (!parseResult) return;

		// Re-validate with resolutions applied
		const results = validateRows(effectiveSchema, parseResult.rows, {
			mappings,
			contextDefaults: parseResult.contextDefaults,
		});

		// Apply resolution decisions to replace __unresolved: placeholders
		applyResolutions(results, resolutionGroups);
		setValidationResults([...results]);
	}, [parseResult, effectiveSchema, mappings, resolutionGroups]);

	const toggleRowSkip = useCallback((rowIndex: number) => {
		setValidationResults((prev) =>
			prev.map((r) =>
				r.rowIndex === rowIndex
					? {
							...r,
							status:
								r.status === 'skipped' ? (r.errors.length > 0 ? 'error' : 'valid') : 'skipped',
						}
					: r
			)
		);
	}, []);

	const editCell = useCallback(
		(rowIndex: number, fieldName: string, newValue: unknown) => {
			setValidationResults((prev) =>
				prev.map((r) => {
					if (r.rowIndex !== rowIndex) return r;
					// Update the resolved row with the new value
					const updated: ValidationResult = {
						...r,
						resolvedRow: { ...r.resolvedRow, [fieldName]: newValue },
					};
					// Re-validate the entire row
					return revalidateRow(effectiveSchema, updated);
				})
			);
		},
		[effectiveSchema]
	);

	const validationStats = useMemo(
		() => (validationResults.length > 0 ? getValidationStats(validationResults) : null),
		[validationResults]
	);

	const unresolvedCount = useMemo(
		() => resolutionGroups.filter((g) => g.resolution.status === 'pending').length,
		[resolutionGroups]
	);

	// ====================================================================
	// Step 6: Submit
	// ====================================================================

	const submit = useCallback(async () => {
		const submittable = getSubmittableRows(validationResults, {
			mappings,
			contextDefaults: parseResult?.contextDefaults,
			schema: effectiveSchema,
		});
		if (submittable.length === 0) {
			setError('No valid rows to submit');
			return;
		}

		const totalBatches = Math.ceil(submittable.length / batchSize);
		const controller = new AbortController();
		abortRef.current = controller;

		setProgress({
			status: 'submitting',
			totalRows: submittable.length,
			processedRows: 0,
			successfulRows: 0,
			failedRows: 0,
			skippedRows: validationResults.filter((r) => r.status === 'skipped').length,
			currentBatch: 0,
			totalBatches,
			results: [],
		});

		const allResults: BulkRowResult[] = [];
		let successCount = 0;
		let failCount = 0;

		try {
			if (effectiveSchema.customSubmit) {
				// ── Custom per-row submit (e.g., two-phase V1+V2 contract creation) ──
				const hdrs = getHeaders();
				for (let i = 0; i < submittable.length; i++) {
					if (controller.signal.aborted) break;

					const row = submittable[i];
					const hasCustomFields = effectiveSchema.fields.some((f) => f.isCustomField);
					let rowData: Record<string, unknown>;
					if (hasCustomFields) {
						const { row: baseRow, customData } = extractCustomData(row.data);
						rowData = {
							...baseRow,
							...(Object.keys(customData).length > 0 ? { customData } : {}),
						};
					} else {
						rowData = { ...row.data };
					}

					try {
						const result = await effectiveSchema.customSubmit({
							row: rowData,
							projectId,
							apiBaseUrl: apiUrlV2 || '',
							headers: hdrs,
							signal: controller.signal,
						});
						allResults.push({
							rowIndex: row.rowIndex,
							status: 'created',
							id: result.id,
						});
						successCount++;
					} catch (err) {
						if (err instanceof Error && err.name === 'AbortError') break;
						allResults.push({
							rowIndex: row.rowIndex,
							status: 'error',
							errors: [err instanceof Error ? err.message : 'Creation failed'],
						});
						failCount++;
					}

					const sc = successCount;
					const fc = failCount;
					setProgress((prev) => ({
						...prev,
						processedRows: i + 1,
						successfulRows: sc,
						failedRows: fc,
						currentBatch: i + 1,
						results: [...allResults],
					}));
				}
			} else {
				// ── Default batch submit via bulk-create endpoint ──
				for (let batch = 0; batch < totalBatches; batch++) {
					if (controller.signal.aborted) break;

					const start = batch * batchSize;
					const end = Math.min(start + batchSize, submittable.length);
					const batchRows = submittable.slice(start, end);

					// Inject projectId into each row; group custom fields into customData
					const hasCustomFields = effectiveSchema.fields.some((f) => f.isCustomField);
					const items = batchRows.map((r) => {
						if (hasCustomFields) {
							const { row, customData } = extractCustomData(r.data);
							return {
								...row,
								projectId,
								...(Object.keys(customData).length > 0 ? { customData } : {}),
							};
						}
						return { ...r.data, projectId };
					});

					try {
						const res = await fetch(`${apiUrlV2}/v2/bulk-create`, {
							method: 'POST',
							headers: getHeaders(),
							body: JSON.stringify({
								entityType: schema.entityType,
								projectId,
								items,
							}),
							signal: controller.signal,
						});

						if (!res.ok) {
							const errorText = await res.text().catch(() => `HTTP ${res.status}`);
							let errorMsg: string;
							try {
								const errorJson = JSON.parse(errorText);
								errorMsg = errorJson.error?.message || errorJson.message || `HTTP ${res.status}`;
							} catch {
								errorMsg = errorText || `HTTP ${res.status}`;
							}
							for (const row of batchRows) {
								allResults.push({
									rowIndex: row.rowIndex,
									status: 'error',
									errors: [errorMsg],
								});
								failCount++;
							}
						} else {
							const data = await res.json();

							if (data.error) {
								// Entire batch failed (application-level error)
								for (const row of batchRows) {
									allResults.push({
										rowIndex: row.rowIndex,
										status: 'error',
										errors: [data.error.message || 'Batch failed'],
									});
									failCount++;
								}
							} else if (data.data?.results) {
								// Per-row results
								for (let i = 0; i < batchRows.length; i++) {
									const rowResult = data.data.results[i];
									if (rowResult?.error) {
										allResults.push({
											rowIndex: batchRows[i].rowIndex,
											status: 'error',
											errors: [rowResult.error],
										});
										failCount++;
									} else {
										allResults.push({
											rowIndex: batchRows[i].rowIndex,
											status: 'created',
											id: rowResult?.id,
										});
										successCount++;
									}
								}
							} else {
								// No per-row results — assume all succeeded
								for (const row of batchRows) {
									allResults.push({ rowIndex: row.rowIndex, status: 'created' });
									successCount++;
								}
							}
						}
					} catch (fetchErr) {
						if (fetchErr instanceof Error && fetchErr.name === 'AbortError') break;
						for (const row of batchRows) {
							allResults.push({
								rowIndex: row.rowIndex,
								status: 'error',
								errors: [fetchErr instanceof Error ? fetchErr.message : 'Network error'],
							});
							failCount++;
						}
					}

					const sc = successCount;
					const fc = failCount;
					setProgress((prev) => ({
						...prev,
						processedRows: end,
						successfulRows: sc,
						failedRows: fc,
						currentBatch: batch + 1,
						results: [...allResults],
					}));
				}
			} // end else (default batch submit)

			setProgress((prev) => ({ ...prev, status: 'complete', results: [...allResults] }));
			if (successCount > 0 && onSuccess) {
				onSuccess();
			}
		} catch (err) {
			setProgress((prev) => ({
				...prev,
				status: 'error',
				error: err instanceof Error ? err.message : 'Upload failed',
			}));
		}
	}, [
		validationResults,
		batchSize,
		projectId,
		apiUrlV2,
		schema,
		getHeaders,
		onSuccess,
		mappings,
		parseResult,
		effectiveSchema,
	]);

	/**
	 * Retry only the rows that failed server-side.
	 * Filters validation results to keep failed rows, resets progress,
	 * and navigates back to validate-preview so the user can review.
	 */
	const retryFailed = useCallback(() => {
		const failedRowIndices = new Set(
			progress.results.filter((r) => r.status === 'error').map((r) => r.rowIndex)
		);
		const createdRowIndices = new Set(
			progress.results.filter((r) => r.status === 'created').map((r) => r.rowIndex)
		);

		// Keep only failed rows; exclude successfully created rows to prevent duplicates
		setValidationResults((prev) =>
			prev
				.filter((r) => failedRowIndices.has(r.rowIndex) && !createdRowIndices.has(r.rowIndex))
				// Reset any that were marked as skipped so they can be retried
				.map((r) => ({
					...r,
					status: r.errors.length > 0 ? ('error' as const) : ('valid' as const),
				}))
		);

		// Reset progress
		setProgress({
			status: 'idle',
			totalRows: 0,
			processedRows: 0,
			successfulRows: 0,
			failedRows: 0,
			skippedRows: 0,
			currentBatch: 0,
			totalBatches: 0,
			results: [],
		});

		// Go back to validate-preview step
		const validateIdx = BULK_UPLOAD_STEPS.indexOf('validate-preview');
		setCurrentStepIndex(validateIdx);
	}, [progress.results]);

	/**
	 * Export failed rows as a CSV file for offline correction.
	 * Includes the original row data + error messages.
	 */
	const exportFailedRows = useCallback(() => {
		const failedResults = progress.results.filter((r) => r.status === 'error');
		if (failedResults.length === 0) return;

		const failedRowIndices = new Set(failedResults.map((r) => r.rowIndex));
		const failedValidation = validationResults.filter((r) => failedRowIndices.has(r.rowIndex));

		// Build CSV: field columns + Error column
		const fieldNames = effectiveSchema.fields.map((f) => f.name);
		const fieldLabels = effectiveSchema.fields.map((f) => f.label);
		const headerRow = [...fieldLabels, 'Error'].map((h) => `"${h}"`).join(',');

		const dataRows = failedValidation.map((vr) => {
			const serverResult = failedResults.find((r) => r.rowIndex === vr.rowIndex);
			const cells = fieldNames.map((name) => {
				const val = vr.resolvedRow[name];
				if (val === null || val === undefined) return '';
				return `"${String(val).replace(/"/g, '""')}"`;
			});
			const errorMsg = serverResult?.errors?.join('; ') || 'Unknown error';
			cells.push(`"${errorMsg.replace(/"/g, '""')}"`);
			return cells.join(',');
		});

		const csv = [headerRow, ...dataRows].join('\n');
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `${schema.entityType}-failed-rows.csv`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	}, [progress.results, validationResults, effectiveSchema, schema.entityType]);

	const saveTemplate = useCallback(
		async (name: string, description?: string) => {
			const columnMappings: TemplateMappingEntry[] = mappings
				.filter((m): m is typeof m & { sourceColumn: string } => m.sourceColumn !== null)
				.map((m) => ({
					sourceColumn: m.sourceColumn,
					targetField: m.targetField,
					transform: null,
				}));

			const payload = {
				projectId,
				entityType: schema.entityType,
				name,
				description,
				columnMappings,
				settings: {
					sheetName: parseResult?.selectedSheet ?? null,
					headerRow: parseResult?.headerDetection.headerRow ?? 1,
					dataStartRow: parseResult?.headerDetection.dataStartRow ?? 2,
					skipEmptyRows: true,
					dateFormat: 'YYYY-MM-DD',
					contextRows: parseResult?.contextDefaults
						.filter((cd) => cd.confirmed)
						.map((cd) => ({
							label: cd.label,
							targetField: cd.targetField,
							value: cd.rawValue,
						})),
				},
			};

			const res = await fetch(`${apiUrlV2}/v2/upload-templates`, {
				method: 'POST',
				headers: getHeaders(),
				body: JSON.stringify(payload),
			});
			const data = await res.json();
			if (data.error) throw new Error(data.error.message);

			// Refresh templates
			await loadTemplates();
		},
		[mappings, projectId, schema.entityType, parseResult, apiUrlV2, getHeaders, loadTemplates]
	);

	// ====================================================================
	// Navigation
	// ====================================================================

	const canGoBack = currentStepIndex > 0;
	const canGoNext = useMemo(() => {
		switch (currentStep) {
			case 'select-template':
				return true; // Optional step
			case 'upload-file':
				return parseResult !== null && !isParsingFile;
			case 'map-columns': {
				// All required fields must be mapped OR satisfied by a confirmed context default
				const contextDefs = parseResult?.contextDefaults ?? [];
				const requiredFields = effectiveSchema.fields.filter((f) => f.required);
				return requiredFields.every(
					(f) =>
						mappings.some((m) => m.targetField === f.name && m.sourceColumn !== null) ||
						contextDefs.some((cd) => cd.confirmed && cd.targetField === f.name)
				);
			}
			case 'resolve-relations':
				return unresolvedCount === 0;
			case 'validate-preview':
				return validationResults.some((r) => r.status === 'valid' || r.status === 'warning');
			case 'submit':
				return false; // Terminal step
			default:
				return false;
		}
	}, [
		currentStep,
		parseResult,
		isParsingFile,
		effectiveSchema.fields,
		mappings,
		unresolvedCount,
		validationResults,
	]);

	const goNext = useCallback(() => {
		if (!canGoNext || currentStepIndex >= BULK_UPLOAD_STEPS.length - 1) return;

		// Trigger step-transition side effects
		const nextStepIndex = currentStepIndex + 1;
		const nextStep = BULK_UPLOAD_STEPS[nextStepIndex];

		if (nextStep === 'resolve-relations') {
			runRelationResolution();
		}
		if (nextStep === 'validate-preview') {
			runFinalValidation();
		}

		setCurrentStepIndex(nextStepIndex);
	}, [canGoNext, currentStepIndex, runRelationResolution, runFinalValidation]);

	const goBack = useCallback(() => {
		if (currentStepIndex > 0) {
			setCurrentStepIndex((prev) => prev - 1);
		}
	}, [currentStepIndex]);

	const goToStep = useCallback(
		(step: BulkUploadStep) => {
			const idx = BULK_UPLOAD_STEPS.indexOf(step);
			if (idx >= 0 && idx <= currentStepIndex) {
				setCurrentStepIndex(idx);
			}
		},
		[currentStepIndex]
	);

	const reset = useCallback(() => {
		setCurrentStepIndex(0);
		setSelectedTemplate(null);
		setFile(null);
		setParseResult(null);
		setIsParsingFile(false);
		setParseError(null);
		setMappings([]);
		setResolutionGroups([]);
		setCreatedLookups({});
		setValidationResults([]);
		setProgress({
			status: 'idle',
			totalRows: 0,
			processedRows: 0,
			successfulRows: 0,
			failedRows: 0,
			skippedRows: 0,
			currentBatch: 0,
			totalBatches: 0,
			results: [],
		});
		setError(null);
		if (abortRef.current) {
			abortRef.current.abort();
			abortRef.current = null;
		}
	}, []);

	const clearError = useCallback(() => setError(null), []);

	// ====================================================================
	// Return
	// ====================================================================

	return {
		currentStep,
		currentStepIndex,
		canGoNext,
		canGoBack,
		goNext,
		goBack,
		goToStep,
		reset,

		templates,
		selectedTemplate,
		isLoadingTemplates,
		selectTemplate,
		loadTemplates,
		handleDownloadTemplate,

		file,
		parseResult,
		isParsingFile,
		parseError,
		handleFileSelect,
		handleSheetChange,
		handleHeaderRowChange,

		mappings,
		updateMapping,

		resolutionGroups,
		resolveGroup,
		skipGroup,
		autoResolveHighConfidence,
		unresolvedCount,
		createRelatedRecord,
		mergedLookupData,

		validationResults,
		validationStats,
		toggleRowSkip,
		editCell,

		progress,
		submit,
		saveTemplate,
		retryFailed,
		exportFailedRows,

		error,
		clearError,

		effectiveSchema,
	};
}
